import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Drawer from '../drawer/drawer';
import Loader from '../loader/loader';
import StatusBadge from '../status-badge/status-badge';
import { useAnalyticsCall } from '@/hooks/use-conversations/use-conversations';
import { deriveStatusVariant } from '../status-badge/status-badge.utils';
import { durationSeconds, formatConversationDateTime } from '@/utils/helpers';
import { CallDetail } from '@/services/call-log-service/call-log-service.types';
import { ConversationDetail } from './conversation-detail';
import styles from './conversation-drawer.module.scss';

interface ConversationDrawerProps {
  callId?: string;
  onClose: () => void;
}

/** The mockup writes 2:21, not 02:21, and the shared helper pads the minute. */
function formatCallLength(duration: CallDetail['duration']) {
  const seconds = durationSeconds(duration);
  if (seconds === null) return null;
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

function DrawerMeta({ call }: { call: CallDetail }) {
  const { t } = useTranslation('conversations');
  const length = formatCallLength(call.duration);

  return (
    <>
      <span>
        {t('drawer.meta.number')} <b>{call.phone}</b>
      </span>
      <span>
        {t('drawer.meta.date')} <b>{formatConversationDateTime(call.created_at)}</b>
      </span>
      <span>
        {t('drawer.meta.duration')}{' '}
        <b>{length === null ? '--' : t('drawer.meta.minutes', { length })}</b>
      </span>
      <StatusBadge variant={deriveStatusVariant(call)} />
    </>
  );
}

/**
 * Detail body for one open call. Split from the drawer so the detail query exists only
 * while a call is open: useAnalyticsCall has no `enabled` guard, so an always-mounted
 * caller would fetch with an empty id and hold a channel subscription on the list.
 */
function DrawerContents({ callId, onClose }: { callId: string; onClose: () => void }) {
  const { t } = useTranslation('conversations');
  const [translate, setTranslate] = useState('false');

  const { analyticsCall, addAnnotation, isAddingAnnotation, analyticsCallIsFetching } =
    useAnalyticsCall({ analytics_call_id: callId, translate });

  // `placeholderData` keeps the previous call's payload across a key change, so guard on
  // the id matching rather than on truthiness alone.
  const call = analyticsCall?.id === Number(callId) ? analyticsCall : undefined;

  return (
    <Drawer
      className={styles.drawer}
      open
      onClose={onClose}
      title={call?.topics?.join(', ') || t('drawer.fallbackTitle')}
      meta={call ? <DrawerMeta call={call} /> : undefined}
    >
      {!call ? (
        <Loader />
      ) : (
        // Keyed as well as unmounted on close: an already-cached call resolves
        // synchronously under staleTime Infinity, so switching calls without closing never
        // hits the unmount and would carry over the playhead, the review tick and
        // translate. The playhead feeds end_second, so that one gets written to the API.
        <ConversationDetail
          key={callId}
          call={call}
          callId={callId}
          translate={translate}
          onTranslateChange={setTranslate}
          isFetching={analyticsCallIsFetching}
          addAnnotation={addAnnotation}
          isAddingAnnotation={isAddingAnnotation}
        />
      )}
    </Drawer>
  );
}

/** The detail view as a slide-over, so the list underneath stays mounted. */
export default function ConversationDrawer({ callId, onClose }: ConversationDrawerProps) {
  // Unmounted rather than hidden: that is what discards per-call state on close, at the
  // cost of the close animation.
  if (!callId) return null;
  return <DrawerContents callId={callId} onClose={onClose} />;
}
